import type { CampaignCreatePayload, CampaignResponse } from '@contracts'
import dayjs from 'dayjs'

export * from './common'
export const ADMIN_LIST_CAMPS_ROUTE = '/campaigns'

// --- Campaign Form Types ---

export interface ConditionItem {
	itemName?: string
	condition?: string
	value?: any
}

export type TriggerEvent = (typeof EVENT_CAMPAIGN)[keyof typeof EVENT_CAMPAIGN]
export type RecurrenceLabel = '毎月' | '毎週' | '毎日' | '月末' | ''
export type RecurrenceValue = 'monthly' | 'weekly' | 'daily' | 'end_of_month'
export type CampType = 'notPointGift' | 'PointGift'
export type CampaignLimit = 'noLimit' | 'Limit'
export type PeriodSetting = 'notPeriod' | 'Period'
export type StatusLabel = '有効' | '無効'
export type ConditionSearch = '設定しない' | '設定する'

export interface CampaignFormValues {
	campName: string
	campType: CampType
	CampCouponId: number
	numberOfPointGift: number
	event: TriggerEvent | ''
	designatedDate: dayjs.Dayjs | null
	designatedTime: dayjs.Dayjs | null
	periodically: RecurrenceLabel
	periodicallyItem: string
	periodicallyTime: dayjs.Dayjs | null
	campaignLimit: CampaignLimit
	numberOfLimit: number
	period: PeriodSetting
	startPeriod: dayjs.Dayjs | null
	endPeriod: dayjs.Dayjs | null
	status: StatusLabel
	conditionSearch: ConditionSearch
	ConditionBox: ConditionItem[]
	memberLoad: { memberId: number }[]
}

export type CampaignApiPayload = CampaignCreatePayload

export interface CampaignDetail extends CampaignResponse {
	couponMaster?: { couponMasterId: number; title: string } | null
}

export function mapFormToPayload(data: Partial<CampaignFormValues>): CampaignApiPayload {
	return {
		name: data.campName ?? '',
		rewardType: data.campType === 'PointGift' ? 'points' : 'coupon',
		masterCouponId:
			data.campType !== 'PointGift' && data.CampCouponId
				? Number(data.CampCouponId)
				: undefined,
		rewardPoints: data.campType === 'PointGift' ? Number(data.numberOfPointGift) : undefined,
		triggerEvent: (data.event as TriggerEvent) || 'check_in',
		limitType: data.campaignLimit === 'Limit' ? 'limited' : 'unlimited',
		usageLimit: data.campaignLimit === 'Limit' ? Number(data.numberOfLimit) : undefined,
		hasActivePeriod: data.period === 'Period' ? 'yes' : 'no',
		startDate: data.startPeriod?.toDate() || undefined,
		endDate: data.endPeriod?.toDate() || undefined,
		scheduledDate: data.designatedDate?.toDate() || undefined,
		scheduledTime: data.designatedTime?.toDate() || undefined,
		recurrenceType: (data.periodically === '毎月'
			? 'monthly'
			: data.periodically === '毎週'
				? 'weekly'
				: data.periodically === '毎日'
					? 'daily'
					: data.periodically === '月末'
						? 'end_of_month'
						: undefined) as RecurrenceValue | undefined,
		recurrenceValue:
			data.periodically === '毎週' && data.periodicallyItem
				? (
						{
							日: '0',
							月: '1',
							火: '2',
							水: '3',
							木: '4',
							金: '5',
							土: '6',
						} as Record<string, string>
					)[data.periodicallyItem] || data.periodicallyItem
				: data.periodicallyItem || undefined,
		recurrenceTime: data.periodicallyTime?.toDate() || undefined,
		status: data.status === '有効' ? 'active' : 'inactive',
		searchConditions: data.conditionSearch,
		conditionDetails: conditionBoxToEngineJson(data.ConditionBox || []),
	}
}

export function mapDetailToForm(raw: CampaignDetail): Partial<CampaignFormValues> {
	return {
		campName: raw.name,
		campType: raw.rewardType === 'points' ? 'PointGift' : 'notPointGift',
		CampCouponId: raw.masterCouponId ?? 0,
		numberOfPointGift: raw.rewardPoints ?? 0,
		event: raw.triggerEvent as TriggerEvent,
		designatedDate: raw.scheduledDate ? dayjs(raw.scheduledDate) : null,
		designatedTime: raw.scheduledTime ? dayjs(raw.scheduledTime) : null,
		periodically:
			raw.recurrenceType === 'monthly'
				? '毎月'
				: raw.recurrenceType === 'weekly'
					? '毎週'
					: raw.recurrenceType === 'daily'
						? '毎日'
						: raw.recurrenceType === 'end_of_month'
							? '月末'
							: '',
		periodicallyItem:
			raw.recurrenceType === 'weekly' && raw.recurrenceValue
				? (
						{
							'0': '日',
							'1': '月',
							'2': '火',
							'3': '水',
							'4': '木',
							'5': '金',
							'6': '土',
						} as Record<string, string>
					)[raw.recurrenceValue] || raw.recurrenceValue
				: (raw.recurrenceValue ?? ''),
		periodicallyTime: raw.recurrenceTime ? dayjs(raw.recurrenceTime) : null,
		startPeriod: raw.startDate ? dayjs(raw.startDate) : null,
		endPeriod: raw.endDate ? dayjs(raw.endDate) : null,
		period: raw.hasActivePeriod === 'yes' ? 'Period' : 'notPeriod',
		campaignLimit: raw.limitType === 'limited' ? 'Limit' : 'noLimit',
		numberOfLimit: raw.usageLimit ?? 0,
		status: raw.status === 'active' ? '有効' : '無効',
		conditionSearch: (raw.searchConditions as ConditionSearch) ?? '設定しない',
		ConditionBox: engineJsonToConditionBox(raw.conditionDetails),
		memberLoad: [],
	}
}

// --- Condition Operator Mappings ---

export const CONDITION_OPERATORS = {
	NUMBER: [
		{ ja: 'が次に等しい', op: 'eq' },
		{ ja: 'が次に等しくない', op: 'neq' },
		{ ja: 'が次以上', op: 'gte' },
		{ ja: 'が次以下', op: 'lte' },
		{ ja: 'が次より大きい', op: 'gt' },
		{ ja: 'が次より小さい', op: 'lt' },
	],
	TEXT: [
		{ ja: 'が次に等しい', op: 'eq' },
		{ ja: 'が次に等しくない', op: 'neq' },
		{ ja: 'が次のいずれかを含む', op: 'contains' },
		{ ja: 'が次のいずれかを含まない', op: 'not_contains' },
	],
	DATE: [
		{ ja: 'が次に等しい', op: 'eq' },
		{ ja: 'が次に等しくない', op: 'neq' },
		{ ja: 'が次の日付以前', op: 'lte' },
		{ ja: 'が次の日付以降', op: 'gte' },
	],
	RADIO: [
		{ ja: 'が次のいずれかを含む', op: 'in' },
		{ ja: 'が次のいずれかを含まない', op: 'not_in' },
	],
	EQUAL: [{ ja: 'が次に等しい', op: 'eq' }],
}

export const LABEL_CONDITON = {
	ID: 'id',
	VISIT_COUNT: 'visitCount',
	MEMBERSHIP_DURATION: 'membershipDuration',
	MEMBER_CODE: 'memberCode',
	NAME_KANJI: 'nameKanji',
	NAME_KANA: 'nameKana',
	PHONE_NUMBER: 'phoneNumber',
	DATE_OF_BIRTH: 'dateOfBirth',
	VISIT_DATE: 'visitDate',
	EXPIRATION_DATE: 'expirationDate',
	JOIN_DATE: 'joinDate',
	GENDER: 'gender',
	MEMBERSHIP_TYPE: 'membershipType',
	BIRTH_MONTH: 'birthMonth',
	// CERTIFICATE_VERIFICATION: 'certificateVerification',
	// REGISTRATION_STATUS: 'registrationStatus',
}

const TEXT_FIELDS: string[] = [LABEL_CONDITON.NAME_KANJI, LABEL_CONDITON.NAME_KANA]
const RADIO_FIELDS: string[] = [LABEL_CONDITON.MEMBERSHIP_TYPE]

function getCategoryForField(field: string): keyof typeof CONDITION_OPERATORS {
	if (RADIO_FIELDS.includes(field)) return 'RADIO'
	if (TEXT_FIELDS.includes(field)) return 'TEXT'
	// ID, VISIT_COUNT, MEMBERSHIP_DURATION, MEMBER_CODE
	if (
		[
			LABEL_CONDITON.ID,
			LABEL_CONDITON.VISIT_COUNT,
			LABEL_CONDITON.MEMBERSHIP_DURATION,
			LABEL_CONDITON.MEMBER_CODE,
		].includes(field)
	)
		return 'NUMBER'
	// VISIT_DATE, EXPIRATION_DATE, JOIN_DATE
	return 'DATE'
}

function buildJaToOp(field: string): Record<string, string> {
	return CONDITION_OPERATORS[getCategoryForField(field)].reduce(
		(acc, { ja, op }) => {
			acc[ja] = op
			return acc
		},
		{} as Record<string, string>,
	)
}

function buildOpToJa(field: string): Record<string, string> {
	return CONDITION_OPERATORS[getCategoryForField(field)].reduce(
		(acc, { ja, op }) => {
			acc[op] = ja
			return acc
		},
		{} as Record<string, string>,
	)
}

/**
 * Convert frontend ConditionBox (itemName, Japanese condition, value)
 * to engine format: { logic: "AND", rules: [{ field, operator, value }] }
 */
export function conditionBoxToEngineJson(items: ConditionItem[]): string {
	if (!items || items.length === 0) return '設定しない'
	const rules = items
		.filter((item) => item.itemName && item.condition)
		.map((item) => {
			let val = item.value
			if (val && typeof val === 'object' && 'format' in val) {
				val = (val as any).format('YYYY-MM-DD')
			}
			return {
				field: item.itemName as string,
				operator: buildJaToOp(item.itemName as string)[item.condition as string] || 'eq',
				value: val ?? '',
			}
		})
	if (rules.length === 0) return '設定しない'
	return JSON.stringify({ logic: 'AND', rules })
}

/**
 * Convert engine format JSON back to frontend ConditionBox items
 */
export function engineJsonToConditionBox(
	conditionDetails: string | null | undefined,
): ConditionItem[] {
	if (!conditionDetails || conditionDetails === '設定しない' || conditionDetails === '{}')
		return []

	try {
		const parsed = JSON.parse(conditionDetails)
		if (!parsed.rules || !Array.isArray(parsed.rules)) return []

		return parsed.rules.map((rule: { field: string; operator: string; value: any }) => {
			let val = rule.value
			if (
				['dateOfBirth', 'visitDate', 'expirationDate', 'joinDate'].includes(rule.field) &&
				val
			) {
				val = dayjs(val as string)
			}
			return {
				itemName: rule.field,
				condition: buildOpToJa(rule.field)[rule.operator] || 'が次に等しい',
				value: val,
			}
		})
	} catch {
		// Legacy format: try parsing as flat array of ConditionItem
		try {
			const legacy = JSON.parse(conditionDetails)
			if (Array.isArray(legacy)) return legacy
		} catch {}
		return []
	}
}

export const getExpiryDateFromCamp = (period: string) => {
	if (period === 'notPeriod') {
		return '無期限'
	}
	return period
}

export const getExpiryDateFromIssuance = (
	row:
		| {
				validityType?: string
				defaultExpiryMonths?: number
				periodStartAt?: string
				periodEndAt?: string
		  }
		| undefined
		| null,
) => {
	if (!row) return ''
	if (row.validityType === 'from_issue_date') {
		return `発行日から ${row.defaultExpiryMonths ?? '-'}ヶ月`
	}
	if (row.validityType === 'specified_period') {
		return `${row.periodStartAt ? dayjs(row.periodStartAt).format('YYYY/MM/DD') : '-'} ~ ${row.periodEndAt ? dayjs(row.periodEndAt).format('YYYY/MM/DD') : '-'}`
	}
	return ''
}

export const LIST_OPTION_CONDITION = [
	{ value: LABEL_CONDITON.ID, label: 'ID' },
	{ value: LABEL_CONDITON.VISIT_COUNT, label: '来館回数' },
	{ value: LABEL_CONDITON.MEMBER_CODE, label: '会員コード' },
	// { value: LABEL_CONDITON.MEMBERSHIP_DURATION, label: '入会期間' },
	{ value: LABEL_CONDITON.NAME_KANJI, label: '氏名（漢字）' },
	{ value: LABEL_CONDITON.NAME_KANA, label: '氏名（カナ）', disabled: true },
	{ value: LABEL_CONDITON.VISIT_DATE, label: '最終来館日' },
	{ value: LABEL_CONDITON.EXPIRATION_DATE, label: '有効期限' },
	// { value: LABEL_CONDITON.JOIN_DATE, label: '入会日' },
	// { value: LABEL_CONDITON.MEMBERSHIP_TYPE, label: '会員種別' },
	// { value: LABEL_CONDITON.CERTIFICATE_VERIFICATION, label: '証明書確認' },
	// { value: LABEL_CONDITON.REGISTRATION_STATUS, label: '登録ステータス' },
]

export const EVENT_CAMPAIGN = {
	REGISTER_NEW: 'new_member_registration',
	RENEW_MEMBERSHIP: 'membership_renewal',
	CHECK_IN: 'check_in',
	SPECIFIED_DATE: 'specific_datetime',
	BIRTHDAY: 'birthday',
	FIRST_DAY_MONTH: 'birth_month_start',
	REGULAR: 'recurring',
	CHANGE_MIGRATION: 'status_transition',
	POINT_EXCHANGE: 'point_exchange',
} as const

export const LIST_EVENT_CAMPAIGN = [
	{ value: EVENT_CAMPAIGN.REGISTER_NEW, label: '新規会員登録' },
	{ value: EVENT_CAMPAIGN.RENEW_MEMBERSHIP, label: '会員更新' },
	{ value: EVENT_CAMPAIGN.CHECK_IN, label: 'チェックイン' },
	{ value: EVENT_CAMPAIGN.SPECIFIED_DATE, label: '指定日時' },
	// { value: EVENT_CAMPAIGN.BIRTHDAY, label: '誕生日' },
	// { value: EVENT_CAMPAIGN.FIRST_DAY_MONTH, label: '誕生月の1日' },
	{ value: EVENT_CAMPAIGN.REGULAR, label: '定期' },
	{ value: EVENT_CAMPAIGN.CHANGE_MIGRATION, label: '既存会員連携時' },
	// { value: EVENT_CAMPAIGN.POINT_EXCHANGE, label: 'ポイント交換' },
]

export const EVENT_CAMPAIGN_LABEL: Record<string, string> = {
	[EVENT_CAMPAIGN.REGISTER_NEW]: '新規会員登録',
	[EVENT_CAMPAIGN.RENEW_MEMBERSHIP]: '会員更新',
	[EVENT_CAMPAIGN.CHECK_IN]: 'チェックイン',
	[EVENT_CAMPAIGN.SPECIFIED_DATE]: '指定日時',
	[EVENT_CAMPAIGN.BIRTHDAY]: '誕生日',
	[EVENT_CAMPAIGN.FIRST_DAY_MONTH]: '誕生月の1日',
	[EVENT_CAMPAIGN.REGULAR]: '定期',
	[EVENT_CAMPAIGN.CHANGE_MIGRATION]: '既存会員連携時',
	[EVENT_CAMPAIGN.POINT_EXCHANGE]: 'ポイント交換',
}

export const MEMBER_STATUS = {
	tempMember: '仮会員',
	member: '会員',
}

export const USER_REGISTRATION_STATUS: Record<string, string> = {
	pending: '審査中',
	approved: '承認済',
	rejected: '却下',
	incomplete: '未完了',
}

export const GET_GENDER_BY_VALUE = (value: string) => {
	switch (value) {
		case 'male':
			return '男性'
		case 'female':
			return '女性'
		default:
			return 'その他'
	}
}

export const MEMBER_TYPE_TEXT: Record<string, string> = {
	red: '一般会員',
	green: '正会員',
	blue: 'LINEのみ',
}
